import { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Wrench, Inbox, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from './api';

// Messagerie côté admin : liste des fils (1 par élève) + conversation + composer.
// L'admin initie les conversations ; il voit aussi les signalements techniques des élèves.
export default function AdminMessages({ token, initialUserId, onUnreadChange }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(initialUserId || null);
  const [thread, setThread] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const d = await apiCall('/admin/messages', 'GET', null, token);
      setThreads(d || []);
      if (onUnreadChange) {
        const total = (d || []).reduce((s, t) => s + (t.unread || 0), 0);
        onUnreadChange(total);
      }
    } catch (e) { /* ignore */ } finally { setLoadingList(false); }
  }, [token, onUnreadChange]);

  const loadThread = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const d = await apiCall(`/admin/messages/${uid}`, 'GET', null, token);
      setThread(d);
    } catch (e) { toast.error('Impossible de charger la conversation'); }
  }, [token]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (active) loadThread(active); }, [active, loadThread]);
  useEffect(() => { bottomRef.current?.scrollIntoView(); }, [thread]);

  const send = async () => {
    const text = body.trim();
    if (!text || !active) return;
    setSending(true);
    try {
      await apiCall(`/admin/messages/${active}`, 'POST', { body: text }, token);
      setBody('');
      await loadThread(active);
      await loadList();
    } catch (e) { toast.error(e.message || 'Envoi impossible'); } finally { setSending(false); }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="admin-messages">
      <div className="grid grid-cols-1 md:grid-cols-3 min-h-[460px]">
        {/* Liste des fils */}
        <div className={`border-r border-white/10 md:col-span-1 ${active ? 'hidden md:block' : ''}`}>
          <div className="p-3 border-b border-white/10 text-sm font-semibold text-white/70 flex items-center gap-2">
            <Inbox size={15} className="text-emerald-400" />Conversations
          </div>
          <div className="max-h-[460px] overflow-y-auto">
            {loadingList ? (
              <div className="p-4 text-white/50 text-sm">Chargement…</div>
            ) : threads.length === 0 ? (
              <div className="p-4 text-white/40 text-sm">Aucune conversation. Ouvre-en une depuis l'onglet « Utilisateurs » → bouton « Contacter ».</div>
            ) : threads.map((t) => (
              <button key={t.user_id} onClick={() => setActive(t.user_id)} data-testid={`thread-${t.user_id}`}
                className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/5 transition ${active === t.user_id ? 'bg-emerald-500/10' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-white truncate">{t.user_name || t.user_email}</span>
                  {t.unread > 0 && <span className="shrink-0 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{t.unread}</span>}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {t.has_report && <Wrench size={11} className="text-amber-300 shrink-0" />}
                  <span className="text-xs text-white/45 truncate">{t.last_sender === 'admin' ? 'Vous : ' : ''}{t.last_body}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation active */}
        <div className="md:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-white/40 text-sm p-6 text-center">
              Sélectionne une conversation à gauche, ou contacte un élève depuis l'onglet « Utilisateurs ».
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-white">{thread?.user?.name || '—'}</div>
                  <div className="text-xs text-white/40">{thread?.user?.email || ''}</div>
                </div>
                <button onClick={() => setActive(null)} className="md:hidden text-xs text-white/50 hover:text-white">← Fils</button>
              </div>

              <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[340px]">
                {(thread?.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                      m.sender === 'admin' ? 'bg-emerald-600/80 text-white rounded-br-sm'
                      : m.kind === 'report' ? 'bg-amber-500/15 border border-amber-400/30 text-amber-50 rounded-bl-sm'
                      : 'bg-white/10 text-white/90 rounded-bl-sm'}`}>
                      {m.kind === 'report' && <div className="text-[10px] font-bold text-amber-300 mb-0.5 flex items-center gap-1"><Wrench size={10} />Problème technique signalé</div>}
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      <div className="text-[9px] text-white/40 mt-1 text-right">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="p-3 border-t border-white/10 flex items-end gap-2">
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={2} placeholder="Écrire à l'élève…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-400/50" data-testid="admin-msg-input" />
                <button onClick={send} disabled={sending || !body.trim()} data-testid="admin-msg-send"
                  className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold px-4 py-2.5 rounded-xl flex items-center gap-1">
                  <Send size={15} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
